import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import NewsPostEditor from '../components/NewsPostEditor';
import NewsPostManager from '../components/NewsPostManager';
import TeamMemberManager from '../components/TeamMemberManager';
import ContactEditor from '../components/ContactEditor';
import apiService from '../services/apiService';
import {
  AdminContainer,
  AdminHeader,
  HeaderContent,
  AdminTitle,
  LogoutButton,
  AdminContent,
  TabNavigation,
  TabButton,
  TabContent,
  ContentCard,
  CardTitle,
  FormGrid,
  FormGroup,
  ImageUpload,
  ImagePreview,
  ActionButtons,
  SaveButton,
  CancelButton,
  SuccessMessage,
  ErrorMessage,
  ServicesList,
  ServiceItem,
  ServiceHeader,
  DeleteButton,
  AddButton
} from '../styles/AdminStyles';

// Login Component
const LoginForm = ({ onLogin, loading, error }) => {
  const [loginData, setLoginData] = useState({
    username: '',
    password: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setLoginData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onLogin(loginData);
  };

  return (
    <AdminContainer style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <ContentCard style={{ maxWidth: '400px', width: '100%' }}>
        <CardTitle style={{ textAlign: 'center', fontSize: '2rem', marginBottom: '30px' }}>
          Admin Login
        </CardTitle>
        
        {error && <ErrorMessage>{error}</ErrorMessage>}
        
        <form onSubmit={handleSubmit}>
          <FormGroup>
            <label htmlFor="username">Username</label>
            <input
              type="text"
              id="username"
              name="username"
              value={loginData.username}
              onChange={handleInputChange}
              placeholder="Enter your username"
              required
            />
          </FormGroup>

          <FormGroup>
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              name="password"
              value={loginData.password}
              onChange={handleInputChange}
              placeholder="Enter your password"
              required
            />
          </FormGroup>

          <SaveButton type="submit" disabled={loading} style={{ width: '100%' }}>
            {loading ? 'Logging in...' : 'Login'}
          </SaveButton>
        </form>
        
        <div style={{ textAlign: 'center', marginTop: '20px' }}>
          <Link 
            to="/" 
            style={{ 
              color: '#4CAF50', 
              textDecoration: 'none', 
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            ← Back to Website
          </Link>
        </div>
      </ContentCard>
    </AdminContainer>
  );
};

// Hero Content Editor
const HeroEditor = () => {
  const [heroData, setHeroData] = useState({
    heading: '',
    subheading: '',
    image: ''
  });
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    fetchHeroContent();
  }, []);

  const fetchHeroContent = async () => {
    try {
      const data = await apiService.getHeroContent();
      setHeroData({
        heading: data.heading,
        subheading: data.subheading,
        image: data.imageUrl // Use imageUrl from API response
      });
    } catch (error) {
      console.error('Error fetching hero content:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setHeroData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setHeroData(prev => ({
          ...prev,
          image: e.target.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    setMessage(null);
    
    try {
      // Send update with imageUrl field name to match backend expectation
      await apiService.updateHeroContent({
        heading: heroData.heading,
        subheading: heroData.subheading,
        imageUrl: heroData.image
      });
      setMessage({ type: 'success', text: 'Hero content updated successfully!' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Error updating hero content. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteImage = async () => {
    if (!window.confirm('Are you sure you want to delete the hero image?')) {
      return;
    }

    setDeleteLoading(true);
    setMessage(null);
    
    try {
      await apiService.deleteHeroImage();
      setHeroData(prev => ({
        ...prev,
        image: ''
      }));
      setMessage({ type: 'success', text: 'Hero image deleted successfully!' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Error deleting hero image. Please try again.' });
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <ContentCard>
      <CardTitle>Hero Section</CardTitle>
      
      {message && (
        message.type === 'success' ? (
          <SuccessMessage>{message.text}</SuccessMessage>
        ) : (
          <ErrorMessage>{message.text}</ErrorMessage>
        )
      )}
      
      <FormGrid>
        <FormGroup>
          <label htmlFor="heading">Heading</label>
          <input
            type="text"
            id="heading"
            name="heading"
            value={heroData.heading}
            onChange={handleInputChange}
            placeholder="Enter hero heading"
          />
        </FormGroup>
        
        <FormGroup>
          <label htmlFor="subheading">Subheading</label>
          <textarea
            id="subheading"
            name="subheading"
            value={heroData.subheading}
            onChange={handleInputChange}
            placeholder="Enter hero subheading"
            rows="3"
          />
        </FormGroup>
      </FormGrid>
      
      <FormGroup>
        <label>Hero Image</label>
        {heroData.image ? (
          <div>
            <ImagePreview style={{ marginBottom: '15px' }}>
              <img src={heroData.image} alt="Hero image" />
            </ImagePreview>
            <ActionButtons>
              <DeleteButton 
                onClick={handleDeleteImage} 
                disabled={deleteLoading}
                style={{ marginRight: '10px' }}
              >
                {deleteLoading ? 'Deleting...' : 'Delete Current Image'}
              </DeleteButton>
              <CancelButton 
                onClick={() => document.getElementById('hero-image').click()}
              >
                Replace Image
              </CancelButton>
            </ActionButtons>
          </div>
        ) : (
          <ImageUpload onClick={() => document.getElementById('hero-image').click()}>
            <input
              type="file"
              id="hero-image"
              accept="image/*"
              onChange={handleImageUpload}
            />
            <p>Click to upload hero image</p>
          </ImageUpload>
        )}
        <input
          type="file"
          id="hero-image"
          accept="image/*"
          onChange={handleImageUpload}
          style={{ display: 'none' }}
        />
      </FormGroup>
      
      <ActionButtons>
        <SaveButton onClick={handleSave} disabled={loading}>
          {loading ? 'Saving...' : 'Save Changes'}
        </SaveButton>
      </ActionButtons>
    </ContentCard>
  );
};

// About Content Editor
const AboutEditor = () => {
  const [aboutData, setAboutData] = useState({
    businessGoal: '',
    aim: '',
    vision: '',
    establishmentAndGrowth: '',
    liberiasAgriculturalLandscapeAndCPCsRole: [],
    operationsAndImpact: [],
    commitmentToLiberiasDevelopment: [],
    whyLiberia: [],
    image: ''
  });
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    fetchAboutContent();
  }, []);

  const fetchAboutContent = async () => {
    try {
      const data = await apiService.getAboutContent();
      setAboutData({
        businessGoal: data.businessGoal || '',
        aim: data.aim || '',
        vision: data.vision || '',
        establishmentAndGrowth: data.establishmentAndGrowth || '',
        liberiasAgriculturalLandscapeAndCPCsRole: data.liberiasAgriculturalLandscapeAndCPCsRole || [],
        operationsAndImpact: data.operationsAndImpact || [],
        commitmentToLiberiasDevelopment: data.commitmentToLiberiasDevelopment || [],
        whyLiberia: data.whyLiberia || [],
        image: data.imageUrl || '' // Use imageUrl from API response
      });
    } catch (error) {
      console.error('Error fetching about content:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setAboutData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleArrayInputChange = (e, fieldName, index) => {
    const { value } = e.target;
    setAboutData(prev => {
      const newArray = [...prev[fieldName]];
      newArray[index] = value;
      return {
        ...prev,
        [fieldName]: newArray
      };
    });
  };

  const addArrayItem = (fieldName) => {
    setAboutData(prev => ({
      ...prev,
      [fieldName]: [...prev[fieldName], '']
    }));
  };

  const removeArrayItem = (fieldName, index) => {
    setAboutData(prev => ({
      ...prev,
      [fieldName]: prev[fieldName].filter((_, i) => i !== index)
    }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setAboutData(prev => ({
          ...prev,
          image: e.target.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    setMessage(null);
    
    try {
      // Send update with imageUrl field name to match backend expectation
      await apiService.updateAboutContent({
        businessGoal: aboutData.businessGoal,
        aim: aboutData.aim,
        vision: aboutData.vision,
        establishmentAndGrowth: aboutData.establishmentAndGrowth,
        liberiasAgriculturalLandscapeAndCPCsRole: aboutData.liberiasAgriculturalLandscapeAndCPCsRole,
        operationsAndImpact: aboutData.operationsAndImpact,
        commitmentToLiberiasDevelopment: aboutData.commitmentToLiberiasDevelopment,
        whyLiberia: aboutData.whyLiberia,
        imageUrl: aboutData.image // Use imageUrl field name to match backend
      });
      setMessage({ type: 'success', text: 'About content updated successfully!' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Error updating about content. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteImage = async () => {
    if (!window.confirm('Are you sure you want to delete the about image?')) {
      return;
    }

    setDeleteLoading(true);
    setMessage(null);
    
    try {
      await apiService.deleteAboutImage();
      setAboutData(prev => ({
        ...prev,
        image: ''
      }));
      setMessage({ type: 'success', text: 'About image deleted successfully!' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Error deleting about image. Please try again.' });
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <ContentCard>
      <CardTitle>About Section</CardTitle>
      
      {message && (
        message.type === 'success' ? (
          <SuccessMessage>{message.text}</SuccessMessage>
        ) : (
          <ErrorMessage>{message.text}</ErrorMessage>
        )
      )}
      
      <FormGroup>
        <label htmlFor="businessGoal">Business Goal</label>
        <textarea
          id="businessGoal"
          name="businessGoal"
          value={aboutData.businessGoal}
          onChange={handleInputChange}
          placeholder="Enter business goal"
          rows="4"
        />
      </FormGroup>
      
      <FormGrid>
        <FormGroup>
          <label htmlFor="aim">Aim</label>
          <textarea
            id="aim"
            name="aim"
            value={aboutData.aim}
            onChange={handleInputChange}
            placeholder="Enter aim"
            rows="3"
          />
        </FormGroup>
        
        <FormGroup>
          <label htmlFor="vision">Vision</label>
          <textarea
            id="vision"
            name="vision"
            value={aboutData.vision}
            onChange={handleInputChange}
            placeholder="Enter vision"
            rows="3"
          />
        </FormGroup>
      </FormGrid>
      
      <FormGroup>
        <label htmlFor="establishmentAndGrowth">Establishment and Growth</label>
        <textarea
          id="establishmentAndGrowth"
          name="establishmentAndGrowth"
          value={aboutData.establishmentAndGrowth}
          onChange={handleInputChange}
          placeholder="Enter establishment and growth details"
          rows="4"
        />
      </FormGroup>
      
      {/* Array field sections */}
      <FormGroup>
        <label>Liberia's Agricultural Landscape and CPC's Role</label>
        {aboutData.liberiasAgriculturalLandscapeAndCPCsRole.map((item, index) => (
          <div key={index} style={{ display: 'flex', marginBottom: '10px' }}>
            <textarea
              value={item}
              onChange={(e) => handleArrayInputChange(e, 'liberiasAgriculturalLandscapeAndCPCsRole', index)}
              placeholder="Enter point about agricultural landscape"
              rows="2"
              style={{ flex: 1 }}
            />
            <DeleteButton 
              onClick={() => removeArrayItem('liberiasAgriculturalLandscapeAndCPCsRole', index)}
              style={{ marginLeft: '10px', height: '40px' }}
            >
              X
            </DeleteButton>
          </div>
        ))}
        <AddButton 
          onClick={() => addArrayItem('liberiasAgriculturalLandscapeAndCPCsRole')}
          style={{ marginTop: '10px' }}
        >
          Add Point
        </AddButton>
      </FormGroup>

      <FormGroup>
        <label>Operations and Impact</label>
        {aboutData.operationsAndImpact.map((item, index) => (
          <div key={index} style={{ display: 'flex', marginBottom: '10px' }}>
            <textarea
              value={item}
              onChange={(e) => handleArrayInputChange(e, 'operationsAndImpact', index)}
              placeholder="Enter operations point"
              rows="2"
              style={{ flex: 1 }}
            />
            <DeleteButton 
              onClick={() => removeArrayItem('operationsAndImpact', index)}
              style={{ marginLeft: '10px', height: '40px' }}
            >
              X
            </DeleteButton>
          </div>
        ))}
        <AddButton 
          onClick={() => addArrayItem('operationsAndImpact')}
          style={{ marginTop: '10px' }}
        >
          Add Point
        </AddButton>
      </FormGroup>

      <FormGroup>
        <label>Commitment to Liberia's Development</label>
        {aboutData.commitmentToLiberiasDevelopment.map((item, index) => (
          <div key={index} style={{ display: 'flex', marginBottom: '10px' }}>
            <textarea
              value={item}
              onChange={(e) => handleArrayInputChange(e, 'commitmentToLiberiasDevelopment', index)}
              placeholder="Enter commitment point"
              rows="2"
              style={{ flex: 1 }}
            />
            <DeleteButton 
              onClick={() => removeArrayItem('commitmentToLiberiasDevelopment', index)}
              style={{ marginLeft: '10px', height: '40px' }}
            >
              X
            </DeleteButton>
          </div>
        ))}
        <AddButton 
          onClick={() => addArrayItem('commitmentToLiberiasDevelopment')}
          style={{ marginTop: '10px' }}
        >
          Add Point
        </AddButton>
      </FormGroup>

      <FormGroup>
        <label>Why Liberia</label>
        {aboutData.whyLiberia.map((item, index) => (
          <div key={index} style={{ display: 'flex', marginBottom: '10px' }}>
            <textarea
              value={item}
              onChange={(e) => handleArrayInputChange(e, 'whyLiberia', index)}
              placeholder="Enter reason for choosing Liberia"
              rows="2"
              style={{ flex: 1 }}
            />
            <DeleteButton 
              onClick={() => removeArrayItem('whyLiberia', index)}
              style={{ marginLeft: '10px', height: '40px' }}
            >
              X
            </DeleteButton>
          </div>
        ))}
        <AddButton 
          onClick={() => addArrayItem('whyLiberia')}
          style={{ marginTop: '10px' }}
        >
          Add Point
        </AddButton>
      </FormGroup>

      {/* Image upload section */}
      <FormGroup>
        <label>About Image</label>
        {aboutData.image ? (
          <div>
            <ImagePreview style={{ marginBottom: '15px' }}>
              <img src={aboutData.image} alt="About image" />
            </ImagePreview>
            <ActionButtons>
              <DeleteButton 
                onClick={handleDeleteImage} 
                disabled={deleteLoading}
                style={{ marginRight: '10px' }}
              >
                {deleteLoading ? 'Deleting...' : 'Delete Current Image'}
              </DeleteButton>
              <CancelButton 
                onClick={() => document.getElementById('about-image').click()}
              >
                Replace Image
              </CancelButton>
            </ActionButtons>
          </div>
        ) : (
          <ImageUpload onClick={() => document.getElementById('about-image').click()}>
            <input
              type="file"
              id="about-image"
              accept="image/*"
              onChange={handleImageUpload}
            />
            <p>Click to upload about image</p>
          </ImageUpload>
        )}
        <input
          type="file"
          id="about-image"
          accept="image/*"
          onChange={handleImageUpload}
          style={{ display: 'none' }}
        />
      </FormGroup>
      
      <ActionButtons>
        <SaveButton onClick={handleSave} disabled={loading}>
          {loading ? 'Saving...' : 'Save Changes'}
        </SaveButton>
      </ActionButtons>
    </ContentCard>
  );
};

// Services Editor
const ServicesEditor = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const data = await apiService.getServicesContent();
      setServices(data);
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  const addService = () => {
    setServices(prev => [...prev, {
      name: '',
      description: '',
      features: []
    }]);
  };

  const updateService = (index, field, value) => {
    setServices(prev => prev.map((service, i) => 
      i === index ? { ...service, [field]: value } : service
    ));
  };

  const deleteService = (index) => {
    setServices(prev => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    setLoading(true);
    setMessage(null);
    
    try {
      await apiService.updateServicesContent(services);
      setMessage({ type: 'success', text: 'Services updated successfully!' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Error updating services. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <ContentCard>
      <CardTitle>Services Section</CardTitle>
      
      {message && (
        message.type === 'success' ? (
          <SuccessMessage>{message.text}</SuccessMessage>
        ) : (
          <ErrorMessage>{message.text}</ErrorMessage>
        )
      )}
      
      <AddButton onClick={addService}>Add New Service</AddButton>
      
      <ServicesList>
        {services.map((service, index) => (
          <ServiceItem key={index}>
            <ServiceHeader>
              <h4>Service {index + 1}</h4>
              <DeleteButton onClick={() => deleteService(index)}>Delete</DeleteButton>
            </ServiceHeader>
            
            <FormGrid>
              <FormGroup>
                <label>Service Name</label>
                <input
                  type="text"
                  value={service.name}
                  onChange={(e) => updateService(index, 'name', e.target.value)}
                  placeholder="Enter service name"
                />
              </FormGroup>
              
              <FormGroup>
                <label>Description</label>
                <textarea
                  value={service.description}
                  onChange={(e) => updateService(index, 'description', e.target.value)}
                  placeholder="Enter service description"
                  rows="3"
                />
              </FormGroup>
            </FormGrid>
          </ServiceItem>
        ))}
      </ServicesList>
      
      <ActionButtons>
        <SaveButton onClick={handleSave} disabled={loading}>
          {loading ? 'Saving...' : 'Save Changes'}
        </SaveButton>
      </ActionButtons>
    </ContentCard>
  );
};

// Main Admin Component
const Admin = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [activeTab, setActiveTab] = useState('hero');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (loginData) => {
    setLoading(true);
    setError('');

    try {
      // For demo purposes, accept any username/password
      // In production, this would validate against the backend
      if (loginData.username && loginData.password) {
        setIsLoggedIn(true);
      } else {
        throw new Error('Invalid credentials');
      }
    } catch (error) {
      setError('Invalid username or password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setActiveTab('hero');
  };

  if (!isLoggedIn) {
    return <LoginForm onLogin={handleLogin} loading={loading} error={error} />;
  }

  const tabs = [
    { id: 'hero', label: 'Hero Section' },
    { id: 'about', label: 'About Us' },
    { id: 'services', label: 'Services' },
    { id: 'team', label: 'Team' },
    { id: 'contact', label: 'Contact' },
    { id: 'news', label: 'News Feed' }
  ];

  return (
    <AdminContainer>
      <AdminHeader>
        <HeaderContent>
          <AdminTitle>CMS Admin Panel</AdminTitle>
          <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
            <Link 
              to="/" 
              style={{ 
                color: '#F5DEB3', 
                textDecoration: 'none', 
                fontSize: '14px',
                fontWeight: '500',
                padding: '8px 16px',
                border: '1px solid #F5DEB3',
                borderRadius: '4px',
                transition: 'all 0.3s ease'
              }}
            >
              ← Back to Website
            </Link>
            <LogoutButton onClick={handleLogout}>Logout</LogoutButton>
          </div>
        </HeaderContent>
      </AdminHeader>
      
      <AdminContent>
        <TabNavigation>
          {tabs.map(tab => (
            <TabButton
              key={tab.id}
              active={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </TabButton>
          ))}
        </TabNavigation>
        
        <TabContent active={activeTab === 'hero'}>
          <HeroEditor />
        </TabContent>
        
        <TabContent active={activeTab === 'about'}>
          <AboutEditor />
        </TabContent>
        
        <TabContent active={activeTab === 'services'}>
          <ServicesEditor />
        </TabContent>
        
        <TabContent active={activeTab === 'team'}>
          <TeamMemberManager />
        </TabContent>
        
        <TabContent active={activeTab === 'contact'}>
          <ContactEditor />
        </TabContent>
        
        <TabContent active={activeTab === 'news'}>
          {activeTab === 'news' && (
            <>
              <NewsPostEditor onPostCreated={() => {
                // This will find and call the refreshPosts method of the NewsPostManager component below
                // when a new post is created
                if (window.refreshNewsPosts) {
                  window.refreshNewsPosts();
                }
              }} />
              <div style={{ marginTop: '30px' }}></div>
              <NewsPostManager ref={(component) => {
                // Store a reference to the component's fetchNewsPosts method globally
                if (component) {
                  window.refreshNewsPosts = component.fetchNewsPosts;
                }
              }} />
            </>
          )}
        </TabContent>
      </AdminContent>
    </AdminContainer>
  );
};

export default Admin;
